
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeagueTableStats } from '../league-table/stats/LeagueTableStats';
import { LeagueTable } from '../league-table/LeagueTable';
import { calculateMatchResults } from '../league-table/stats/utils/matchResults';
import { MatchResultsCard } from '../league-table/stats/components/MatchResultsCard';
import { HalfTimeMatchResultsCard } from '../league-table/stats/components/HalfTimeMatchResultsCard';
import { calculateDetailedHalfTimeStats } from './utils/halfTimeStats';
import { HalfTimeStatsCard } from './stats/HalfTimeStatsCard';

interface LeagueTableViewProps {
  data: any[];
  filteredAndSortedData: any[];
  onSort: (column: string) => void;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  selectedCountry?: string;
  selectedLeague?: string;
  selectedYear?: string;
  setSelectedCountry?: (country: string) => void;
  setSelectedLeague?: (league: string) => void;
  setSelectedYear?: (year: string) => void;
  countries?: string[];
  leagues?: string[];
  years?: string[];
}

const LeagueTableView: React.FC<LeagueTableViewProps> = ({
  data,
  filteredAndSortedData,
  onSort,
  sortColumn,
  sortDirection,
  selectedCountry,
  selectedLeague,
  selectedYear,
  setSelectedCountry,
  setSelectedLeague,
  setSelectedYear,
  countries,
  leagues,
  years
}) => {
  const navigate = useNavigate();
  const matchResults = calculateMatchResults(filteredAndSortedData);
  const htMatchResults = calculateMatchResults(filteredAndSortedData, true);
  const htStats = calculateDetailedHalfTimeStats(filteredAndSortedData);

  const stats = useLeagueTableStats({
    data: filteredAndSortedData,
    viewType: 'all',
    timeType: 'fullTime'
  });

  const isDrawScoresObject = matchResults.drawScores && typeof matchResults.drawScores === 'object' && 'all' in matchResults.drawScores;
  const formattedDrawScores = isDrawScoresObject ? matchResults.drawScores : {
    all: [],
    higher: [],
    neutral: []
  };

  const isHtDrawScoresObject = htMatchResults.drawScores && typeof htMatchResults.drawScores === 'object' && 'all' in htMatchResults.drawScores;
  const formattedHtDrawScores = isHtDrawScoresObject ? htMatchResults.drawScores : {
    all: [],
    higher: [],
    neutral: []
  };

  return (
    <div className="space-y-4">
      <LeagueTable 
        data={filteredAndSortedData} 
        country={selectedCountry || ''} 
        league={selectedLeague || ''} 
        year={selectedYear || ''} 
      />

      {filteredAndSortedData.length > 0 && (
        <div className="mt-8 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <HalfTimeStatsCard
              title="Leading at HT"
              stats={htStats.leading}
              totalMatches={filteredAndSortedData.length}
              colorScheme={{
                bgColor: 'bg-[#EBF4FF]',
                barColor: 'bg-[#1E40AF]'
              }}
            />
            <HalfTimeStatsCard
              title="Drawing at HT"
              stats={htStats.drawing}
              totalMatches={filteredAndSortedData.length}
              colorScheme={{
                bgColor: 'bg-[#F3F4FF]',
                barColor: 'bg-[#3B4FD1]'
              }}
            />
            <HalfTimeStatsCard
              title="Losing at HT"
              stats={htStats.losing}
              totalMatches={filteredAndSortedData.length}
              colorScheme={{
                bgColor: 'bg-[#F8F9FF]',
                barColor: 'bg-[#6B7ADD]'
              }}
            />
          </div>

          <MatchResultsCard 
            homeWinPercentage={matchResults.homeWinPercentage}
            drawPercentage={matchResults.drawPercentage}
            awayWinPercentage={matchResults.awayWinPercentage}
            popularScores={matchResults.popularScores}
            homeWinScores={matchResults.homeWinScores}
            awayWinScores={matchResults.awayWinScores}
            drawScores={formattedDrawScores}
          />
          <HalfTimeMatchResultsCard 
            homeWinPercentage={htMatchResults.homeWinPercentage}
            drawPercentage={htMatchResults.drawPercentage}
            awayWinPercentage={htMatchResults.awayWinPercentage}
            popularScores={htMatchResults.popularScores}
            homeWinScores={htMatchResults.homeWinScores}
            awayWinScores={htMatchResults.awayWinScores}
            drawScores={formattedHtDrawScores}
          />
        </div>
      )}
    </div>
  );
};

export default LeagueTableView;
